import { useCallback, useMemo } from "react";
import {
  type FlightPriceFreezeVoucher,
  PriceFreezeExpandedVoucherStateEnum,
} from "@b2bportal/core-types";
import {
  ensureExhaustive,
  getPriceFreezeExpandedVoucherState,
  getPriceFreezeExpiryDate,
} from "@b2bportal/core-utilities";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";

const DATE_FORMAT = DateTimeFormatStyle.ShortMonthDayShortWeekday;
const SHORT_TIME_FORMAT = DateTimeFormatStyle.ShortTime;

export const usePriceFreezeCardTag = (
  priceFreezeVoucher: FlightPriceFreezeVoucher
) => {
  const {
    updatedAt: voucherUpdatedAt,
    refundDetails,
    exerciseDetails,
  } = priceFreezeVoucher;

  const { t, formatDateTime } = useI18nContext();

  const expandedVoucherState =
    getPriceFreezeExpandedVoucherState(priceFreezeVoucher);

  const formatCardTagDateTime = useCallback(
    (rawDate: Date | string | undefined) => {
      if (rawDate == null) {
        return undefined;
      }
      const date = new Date(rawDate);
      return {
        date: formatDateTime(date, DATE_FORMAT),
        time: formatDateTime(date, SHORT_TIME_FORMAT),
        interpolation: { escapeValue: false },
      };
    },
    [formatDateTime]
  );

  const voucherExpiry = getPriceFreezeExpiryDate(priceFreezeVoucher);
  const voucherRefundedAt = refundDetails?.refundedAt;
  const voucherExercisedAt = exerciseDetails?.exercisedAt;

  const tagCopy = useMemo(() => {
    switch (expandedVoucherState) {
      case PriceFreezeExpandedVoucherStateEnum.Active:
        return t(
          "priceFreeze.frozenPriceExpirationString",
          formatCardTagDateTime(voucherExpiry)
        );
      case PriceFreezeExpandedVoucherStateEnum.Expired:
        return t(
          "priceFreeze.frozenPriceExpiredString",
          formatCardTagDateTime(voucherExpiry)
        );
      case PriceFreezeExpandedVoucherStateEnum.Cancelled:
        return t(
          "priceFreeze.frozenPriceCancelledString",
          formatCardTagDateTime(voucherUpdatedAt)
        );
      case PriceFreezeExpandedVoucherStateEnum.Refunded:
        return t(
          "priceFreeze.frozenPriceRefundedString",
          formatCardTagDateTime(voucherRefundedAt)
        );
      case PriceFreezeExpandedVoucherStateEnum.Exercised:
        return t(
          "priceFreeze.frozenPriceBookedString",
          formatCardTagDateTime(voucherExercisedAt)
        );
      case PriceFreezeExpandedVoucherStateEnum.Pending:
        return t("priceFreeze.frozenPricePendingString");

      default:
        ensureExhaustive(expandedVoucherState);
    }
  }, [
    t,
    voucherExpiry,
    voucherUpdatedAt,
    voucherRefundedAt,
    voucherExercisedAt,
    expandedVoucherState,
    formatCardTagDateTime,
  ]);

  return {
    tagCopy,
    voucherState: expandedVoucherState,
  };
};
